<template>
  <div class="learner-profile-page">
    <header class="page-header">
      <h1 class="page-title">{{ $t('learnerProfile.title') }}</h1>
      <p class="page-subtitle">{{ $t('learnerProfile.subtitle') }}</p>
    </header>

    <ProfileOnboarding v-if="onboardingOpen" @complete="finishOnboarding" />
    <template v-else>
    <section class="settings-group">
      <h2 class="group-title">{{ $t('learnerProfile.levelsGroup') }}</h2>
      <div class="group-body skill-columns">
        <section v-for="skill in skills" :key="skill.field" class="skill-setting" :aria-labelledby="`profile-${skill.key}`">
          <header class="skill-header">
            <h3 :id="`profile-${skill.key}`">{{ $t(`learnerProfile.${skill.key}`) }}</h3>
            <span class="level-source">{{ draft[skill.field]
              ? $t(`learnerProfile.sourceLabels.${draft[skill.source]}`)
              : $t('learnerProfile.notSet') }}</span>
          </header>
          <LearnerProfileForm :model-value="draft" :step="skill.key" compact bare @update:model-value="change" />
          <div class="skill-test-entry">
            <span class="test-meta">{{ $t(`levelTest.${skill.key}Meta`) }}</span>
            <button type="button" class="test-button" @click="navigateToView('LevelTest', { skill: skill.key })">
              {{ $t(`learnerProfile.${skill.key}TestAction`) }}
              <span class="material-icons" aria-hidden="true">arrow_forward</span>
            </button>
          </div>
        </section>
      </div>
    </section>

    <div class="settings-columns">
    <section class="settings-group">
      <h2 class="group-title">
        {{ $t('learnerProfile.interestsGroup') }}
        <span v-if="draft.interests.length" class="group-count">{{ draft.interests.length }}</span>
      </h2>
      <div class="group-body padded">
        <LearnerProfileForm :model-value="draft" step="interests" bare @update:model-value="change" />
      </div>
      <p class="group-footnote">{{ $t('learnerProfile.interestsHint') }}</p>
    </section>

    <section class="settings-group">
      <h2 class="group-title">{{ $t('learnerProfile.goalsGroup') }}</h2>
      <div class="group-body">
        <LearnerProfileForm :model-value="draft" step="goals" bare @update:model-value="change" />
      </div>
    </section>
    </div>

    <footer class="page-footer">
      <p class="save-status" aria-live="polite">
        <span v-if="justSaved" class="saved">
          <span class="material-icons" aria-hidden="true">check</span>{{ $t('learnerProfile.savedState') }}
        </span>
        <span v-else-if="dirty">{{ $t('learnerProfile.unsavedHint') }}</span>
        <span v-else-if="!user">{{ $t('learnerProfile.syncHint') }}</span>
      </p>
      <button type="button" class="save-button" :disabled="!dirty || saving" @click="save">
        {{ saving ? $t('learnerProfile.saving') : $t('learnerProfile.saveAction') }}
      </button>
    </footer>
    </template>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import LearnerProfileForm from './LearnerProfileForm.vue'
import ProfileOnboarding from './ProfileOnboarding.vue'
import { useAuth } from '@/composables/auth'
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { useViews } from '@/composables/views'

export default {
  name: 'LearnerProfilePage',
  components: { LearnerProfileForm, ProfileOnboarding },
  setup() {
    const { user } = useAuth()
    const { profile, saveProfile, showOnboarding } = useLearnerProfile()
    const { handleViewChange, continueAfterOnboarding } = useViews()
    return { user, profile, saveProfile, showOnboarding, navigateToView: handleViewChange, continueAfterOnboarding }
  },
  data() {
    return {
      draft: { ...this.profile },
      onboardingOpen: false,
      skills: [
        { field: 'listeningLevel', source: 'listeningSource', key: 'listening' },
        { field: 'speakingLevel', source: 'speakingSource', key: 'speaking' }
      ],
      dirty: false,
      saving: false,
      justSaved: false
    }
  },
  watch: {
    showOnboarding: {
      immediate: true,
      handler(show) {
        if (show) this.onboardingOpen = true
      }
    },
    // 登录后账号档案晚一点才到；还有没保存的修改时不覆盖
    profile(next) {
      if (!this.dirty) this.draft = { ...next }
    }
  },
  methods: {
    finishOnboarding() {
      this.onboardingOpen = false
      this.continueAfterOnboarding(this.$t('audioPlayer.demoVideoName'))
    },
    // 修改只改草稿，点页面底部的保存按钮才写入
    change(next) {
      this.draft = next
      this.dirty = true
      this.justSaved = false
    },
    async save() {
      this.saving = true
      try {
        await this.saveProfile(this.draft)
        this.dirty = false
        this.justSaved = true
      } catch (error) {
        ElMessage.error(this.$t('learnerProfile.saveFailed'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.learner-profile-page {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-3xl);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.15;
}

.page-subtitle {
  margin: var(--space-2) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-md);
  line-height: 1.55;
}

/* 分组：小标题在容器外，容器内是细线分隔的行 */
.settings-group + .settings-group,
.settings-group + .settings-columns {
  margin-top: var(--space-8);
}

/* 宽屏时听力和口语并排、领域和目标并排，整页一屏左右就能看完 */
.settings-columns {
  display: grid;
  gap: var(--space-8);
}

.settings-columns .settings-group + .settings-group {
  margin-top: 0;
}

@media (min-width: 1180px) {
  .skill-columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .skill-columns .skill-setting + .skill-setting {
    border-top: none;
    border-left: 1px solid var(--border-subtle);
  }

  .settings-columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-6);
    align-items: start;
  }
}

.group-title {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0 0 var(--space-2) var(--space-4);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.group-count {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.group-body {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  overflow: hidden;
}

.group-body.padded {
  padding: var(--space-4);
}

.group-footnote {
  margin: var(--space-2) var(--space-4) 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.skill-setting {
  padding: var(--space-5);
}

.skill-setting + .skill-setting {
  border-top: 1px solid var(--border-subtle);
}

.skill-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.skill-header h3 {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  font-weight: 600;
}

.skill-test-entry {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2) var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-2);
  border-top: 1px solid var(--border-subtle);
}

.test-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
  margin-left: auto;
  padding: 0 var(--space-2);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.test-button:hover {
  background: var(--surface-subtle);
}

.test-button:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.test-button .material-icons {
  color: var(--ink-secondary);
  font-size: 16px;
}

.test-meta,
.level-source {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

/* 保存栏贴在窗口底部，页面再长也随时能点到 */
.page-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-4);
  margin-top: var(--space-8);
  padding: var(--space-3) 0;
  border-top: 1px solid var(--border-subtle);
  background: var(--surface-canvas);
}

.save-status {
  flex: 1;
  margin: 0 0 0 var(--space-4);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.save-button {
  min-height: 44px;
  padding: 0 var(--space-5);
  border: 1px solid var(--ink-primary);
  border-radius: var(--radius-md);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.save-button:disabled {
  border-color: var(--border-default);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.save-button:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.saved {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--signal-active-strong);
}

.saved .material-icons {
  font-size: 14px;
}

@media (max-width: 640px) {
  .page-title {
    font-size: var(--font-size-2xl);
  }

  .skill-setting {
    padding: var(--space-4);
  }

}

</style>
